import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

// Import the consolidated static content
import { awarenessEventContent } from './AwarenessEventContent';

// Import placeholder components for sub-tabs
import AwarenessDetailsTab from './AwarenessDetailsTab';
import AwarenessActivitiesTab from './AwarenessActivitiesTab';
import AwarenessSpeechTab from './AwarenessSpeechTab';
import AwarenessProjectsTab from './AwarenessProjectsTab';

import 'bootstrap/dist/css/bootstrap.min.css';
 
// ADDED new imports for thunks and userStore
import {
  fetchHolidaysThunk,
  fetchSessionsThunk,
  fetchEventsThunk,
  fetchCompetitionsThunk,
  fetchExamsThunk,
  fetchPtmsThunk,
  fetchCelebrationsThunk,
  fetchAwarenessThunk,
} from '../redux/academicCalendarSlice'; // Adjust path if necessary based on your project structure
import userStore from '../../../zustand/userStore';// Adjust path if necessary

const AwarenessDetailsPage = () => {
  const { idOrDate } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch(); // ADDED dispatch initialization
  const userData = userStore((state) => state.user); // ADDED userData retrieval

  const academicCalendarData = useSelector(
    (state) => state.academicCalendar.academicData
  );
  const [awarenessItem, setAwarenessItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');

  // New state to hold the specific content for the current awareness event
  const [currentEventContent, setCurrentEventContent] = useState(null);

  useEffect(() => {
    // NEW FUNCTIONALITY START: Fetch data if academicCalendarData is empty on refresh
    if (academicCalendarData.length === 0 && userData.id) {
      console.log("AwarenessDetailsPage: academicCalendarData is empty on refresh. Fetching all data...");
      setIsLoading(true); // Keep loading spinner visible while fetching
      dispatch(fetchHolidaysThunk({ user_id: userData.id }));
      dispatch(fetchSessionsThunk());
      dispatch(fetchEventsThunk());
      dispatch(fetchCompetitionsThunk({ user_id: userData.id }));
      dispatch(fetchExamsThunk({ user_id: userData.id }));
      dispatch(fetchPtmsThunk({ user_id: userData.id }));
      dispatch(fetchCelebrationsThunk({ user_id: userData.id }));
      dispatch(fetchAwarenessThunk({ user_id: userData.id }));
      return; // Exit this useEffect run; it will re-run when data loads
    }
    // NEW FUNCTIONALITY END

    if (academicCalendarData.length > 0) {
      const foundItem = academicCalendarData.find(item => {
        if (item.type === 'awarenessDay') {
          const idMatch = String(item.awareness_id || item.id) === String(idOrDate);
          const dateMatch = item.date && moment(item.date, "DD-MM-YYYY").format("YYYY-MM-DD") === idOrDate;
          return idMatch || dateMatch;
        }
        return false;
      });

      setAwarenessItem(foundItem);
      setIsLoading(false);

      // --- Determine and set the specific content for the found item ---
      if (foundItem) {
        const eventDateKey = moment(foundItem.date, "DD-MM-YYYY").format("MM-DD");
        setCurrentEventContent(awarenessEventContent[eventDateKey] || null);
      } else {
        setCurrentEventContent(null);
      }
    }
  }, [idOrDate, academicCalendarData, dispatch, userData.id]); // ADDED dispatch, userData.id to dependency array

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!awarenessItem) {
    return (
      <div className="alert alert-warning text-center" role="alert" style={{ margin: '20px' }}>
        Awareness Day not found for: {idOrDate}
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex align-items-center gap-3 mb-3">
        <button className="px-2 py-1 btn btn-primary" onClick={() => navigate(-1)}>
          Go Back
        </button>
        <h6 className="text-primary mb-0">{awarenessItem.name || awarenessItem.details}</h6>
      </div>

       <div className='d-flex gap-3 mb-2'>
          <button
            className={`btn px-3 py-1  nav-link ${activeTab === 'details' ? 'active btn-primary text-white' : 'btn-light'}`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
         
          <button
            className={`btn px-3 py-1  nav-link ${activeTab === 'activities' ? 'active btn-primary text-white' : 'btn-light'}`}
            onClick={() => setActiveTab('activities')}
          >
            Activities
          </button>
         
          <button
            className={`btn px-3 py-1  nav-link ${activeTab === 'speech' ? 'active btn-primary text-white' : 'btn-light'}`}
            onClick={() => setActiveTab('speech')}
          >
            Speech
          </button>
         
          <button
            className={`btn px-3 py-1 nav-link ${activeTab === 'projects' ? 'active btn-primary text-white' : 'btn-light'}`}
            onClick={() => setActiveTab('projects')}
          >
            List of Projects
          </button>
      </div>

      <div className="tab-content">
        {activeTab === 'details' && <AwarenessDetailsTab item={awarenessItem} eventContent={currentEventContent?.details} />}
        {activeTab === 'activities' && <AwarenessActivitiesTab item={awarenessItem} eventContent={currentEventContent?.activities} />}
        {activeTab === 'speech' && <AwarenessSpeechTab item={awarenessItem} eventContent={currentEventContent?.speeches} />}
        {activeTab === 'projects' && <AwarenessProjectsTab item={awarenessItem} eventContent={currentEventContent?.projects} />}
      </div>
    </div>
  );
};

export default AwarenessDetailsPage; 